import { useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Banknote, CopyCheck, FileSpreadsheet, Landmark, Trash2, Upload } from 'lucide-react';
import {
  useBankStatements,
  useDeleteBankStatement,
  useUploadStatement,
} from '../hooks/useBankStatements';
import type {
  ReconSource,
  StatementSortField,
  StatementSortToken,
  StatementSummary,
  UploadResult,
} from '../lib/bankStatementsApi';
import { SortHeader, sortHeaderProps } from '../components/ui/SortHeader';
import { DateField } from '../components/ui/DateField';
import { formatCurrency, formatDate } from '../lib/format';
import { FadeIn, SkeletonTable } from '../components/ui/Skeleton';
import { UploadProgressList } from '../components/ui/UploadProgress';
import { useUploadQueue } from '../hooks/useUploadQueue';
import { AddCashPaymentModal } from '../components/financials/AddCashPaymentModal';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import { Select } from '../components/ui/Select';
import { useCompanies } from '../hooks/useCompanies';
import { useI18n } from '../contexts/I18nContext';
import { ReconciliationImportDialog } from '../components/bankStatements/ReconciliationImportDialog';
import { usePermissions } from '../hooks/usePermissions';

const STATUS_BADGE: Record<StatementSummary['status'], { label: string; cls: string }> = {
  DRAFT: { label: 'Nereconciliat', cls: 'bg-gray-100 text-gray-600' },
  PARTIALLY_RECONCILED: { label: 'Parțial', cls: 'bg-amber-500/15 text-amber-400' },
  RECONCILED: { label: 'Reconciliat', cls: 'bg-emerald-500/15 text-emerald-400' },
};

function period(s: StatementSummary): string {
  if (!s.periodStart && !s.periodEnd) return '—';
  return `${formatDate(s.periodStart)} – ${formatDate(s.periodEnd)}`;
}

// Period filter modes: everything, one month, one year, or a free interval.
// Month and year are expanded client-side to inclusive `from`/`to` day bounds,
// so the server keeps a single interval filter.
type PeriodMode = 'all' | 'month' | 'year' | 'custom';
const PERIOD_MODES: PeriodMode[] = ['all', 'month', 'year', 'custom'];
const YEARS_BACK = 6;

function dayKey(year: number, month: number, day: number): string {
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function lastDayOfMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

function monthNames(locale: string): string[] {
  const fmt = new Intl.DateTimeFormat(locale, { month: 'long', timeZone: 'UTC' });
  return Array.from({ length: 12 }, (_, i) => {
    const name = fmt.format(new Date(Date.UTC(2024, i, 1)));
    return name.charAt(0).toUpperCase() + name.slice(1);
  });
}

/** The inclusive `YYYY-MM-DD` bounds the list is filtered by, per mode. */
function periodBounds(
  mode: PeriodMode,
  year: number,
  month: number,
  from: string,
  to: string,
): { from?: string; to?: string } {
  switch (mode) {
    case 'month':
      return { from: dayKey(year, month, 1), to: dayKey(year, month, lastDayOfMonth(year, month)) };
    case 'year':
      return { from: dayKey(year, 1, 1), to: dayKey(year, 12, 31) };
    case 'custom':
      return { from: from || undefined, to: to || undefined };
    default:
      return {};
  }
}

export function BankStatementsPage() {
  const { t, locale } = useI18n();
  const [page, setPage] = useState(1);
  // Header sort (default newest upload first) + the period filter: a mode plus
  // the month/year picks and the free interval, kept so switching modes back
  // and forth does not lose what was chosen.
  const [sort, setSort] = useState<StatementSortToken>('-createdAt');
  const now = new Date();
  const [mode, setMode] = useState<PeriodMode>('all');
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const bounds = periodBounds(mode, year, month, from, to);
  const filtered = mode !== 'all' && Boolean(bounds.from || bounds.to);
  const years = Array.from({ length: YEARS_BACK }, (_, i) => now.getFullYear() - i);
  const months = monthNames(locale);
  const [error, setError] = useState<string | null>(null);
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null);
  const [cashModalOpen, setCashModalOpen] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  // Statement pending delete confirmation (dialog open when non-null).
  const [deleteTarget, setDeleteTarget] = useState<StatementSummary | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { can } = usePermissions();
  const canImport = can('financials', 'canEditInvoices');
  const [reconImportOpen, setReconImportOpen] = useState(false);
  // „din import” filter: provenance of the lines + the import batch (the
  // import-history page links here with ?importBatchId=).
  const [searchParams, setSearchParams] = useSearchParams();
  const importBatchId = searchParams.get('importBatchId') ?? undefined;
  const sourceParam = searchParams.get('source');
  const source: ReconSource | undefined =
    sourceParam === 'import' || sourceParam === 'manual' || sourceParam === 'auto'
      ? sourceParam
      : undefined;
  function patchProvenance(next: { source?: string; importBatchId?: string | null }) {
    const params = new URLSearchParams(searchParams);
    if (next.source !== undefined) {
      if (next.source) params.set('source', next.source);
      else params.delete('source');
    }
    if (next.importBatchId !== undefined) {
      if (next.importBatchId) params.set('importBatchId', next.importBatchId);
      else params.delete('importBatchId');
    }
    setSearchParams(params, { replace: true });
    setPage(1);
  }

  const { data, isLoading, isError } = useBankStatements(page, 20, {
    sort,
    from: bounds.from,
    to: bounds.to,
    source,
    importBatchId,
  });
  const uploadMutation = useUploadStatement();
  const deleteMutation = useDeleteBankStatement();

  function header(field: StatementSortField) {
    return sortHeaderProps(sort, field, (token) => {
      setSort(token);
      setPage(1);
    });
  }

  function patchRange(next: { from?: string; to?: string }) {
    if (next.from !== undefined) setFrom(next.from);
    if (next.to !== undefined) setTo(next.to);
    setPage(1);
  }

  function patchPeriod(next: { mode?: PeriodMode; year?: number; month?: number }) {
    if (next.mode !== undefined) setMode(next.mode);
    if (next.year !== undefined) setYear(next.year);
    if (next.month !== undefined) setMonth(next.month);
    setPage(1);
  }
  // Upload progress for the statement PDF. The transfer is the short half —
  // the server then parses the PDF and matches every transaction — so the row
  // switches to its indeterminate phase and stays up until the result lands.
  const { uploads, run } = useUploadQueue();

  // Firm scope for the upload (multi-company): '' = auto-detect by the CUI
  // parsed from the PDF. Hidden with ≤1 firm.
  const { data: companies } = useCompanies();
  const multiFirm = (companies?.length ?? 0) > 1;
  const [uploadCompanyId, setUploadCompanyId] = useState('');

  const rows = data?.rows ?? [];
  const meta = data?.meta;

  async function handleFile(file: File) {
    setError(null);
    setUploadResult(null);
    if (!/\.pdf$/i.test(file.name)) {
      setError('Te rog încarcă un fișier PDF.');
      return;
    }
    try {
      const result = await run(file, (onProgress) =>
        uploadMutation.mutateAsync({
          file,
          companyId: uploadCompanyId || undefined,
          onProgress,
        }),
      );
      // If we skipped or flagged any duplicates — or the firm couldn't be
      // detected from the PDF (multi-company) — show the summary so the user
      // understands what happened before moving on. Otherwise go straight to
      // the reconcile page as before (CLE-152).
      const companyWarning = multiFirm && result.companyMatch === 'none';
      const bankAccountSuggestion = result.bankAccountMatch === 'new_suggested';
      if (
        result.duplicatesSkipped > 0 ||
        result.possibleDuplicates > 0 ||
        result.previousUpload ||
        companyWarning ||
        bankAccountSuggestion
      ) {
        setUploadResult(result);
      } else {
        navigate(`/financials/bank-statements/${result.statementId}/reconcile`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Încărcarea a eșuat.');
    }
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    setDeleteError(null);
    try {
      const result = await deleteMutation.mutateAsync(deleteTarget.id);
      setDeleteTarget(null);
      setNotice(
        t('bankStatements.delete.success', {
          financials: result.deletedFinancials,
          invoices: result.revertedInvoices,
        }),
      );
    } catch (err) {
      // Keep the dialog open so the user sees what went wrong.
      setDeleteError(err instanceof Error ? err.message : t('bankStatements.delete.error'));
    }
  }

  return (
    <div className="w-full p-4 sm:p-6 lg:p-8">
      {/* Header */}
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center gap-2">
          <Landmark className="text-brand-600" size={24} />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Extrase bancare</h1>
            <p className="text-sm text-gray-500">
              Încarcă extrasul PDF; analizăm tranzacțiile și le reconciliem automat.
            </p>
          </div>
        </div>
        {/* Below lg: the company picker and the Excel import across the row,
            cash + upload as two equal halves, instead of a ragged wrap. */}
        <div className="grid grid-cols-2 items-end gap-2 lg:flex lg:flex-wrap">
          {multiFirm && companies && (
            <Select
              label={t('company.switcher.label')}
              value={uploadCompanyId}
              onChange={setUploadCompanyId}
              options={[
                { value: '', label: t('bank.company.auto') },
                ...companies.map((c) => ({
                  value: c.id,
                  label: c.displayName || c.legalName,
                })),
              ]}
              className="col-span-2 lg:w-48"
            />
          )}
          {canImport && (
            <button
              type="button"
              onClick={() => setReconImportOpen(true)}
              className="col-span-2 inline-flex items-center justify-center gap-1.5 rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <FileSpreadsheet size={16} /> Importă reconcilierea din Excel
            </button>
          )}
          <button
            type="button"
            onClick={() => setCashModalOpen(true)}
            className="inline-flex items-center justify-center gap-1.5 rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <Banknote size={16} /> {t('cash.addButton')}
          </button>
          <input
            ref={inputRef}
            type="file"
            accept="application/pdf,.pdf"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void handleFile(file);
              e.target.value = '';
            }}
          />
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploadMutation.isPending}
            className="inline-flex items-center justify-center gap-1.5 rounded-md bg-brand-600 px-3 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
          >
            {uploadMutation.isPending ? (
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
            ) : (
              <Upload size={16} />
            )}
            {uploadMutation.isPending ? 'Se procesează…' : 'Încarcă extras (PDF)'}
          </button>
        </div>
      </div>

      {reconImportOpen && (
        <ReconciliationImportDialog
          statements={rows.map((r) => ({
            id: r.id,
            label: `${r.originalFilename ?? r.bankName} · ${r.periodStart ?? ''}`,
          }))}
          onClose={() => setReconImportOpen(false)}
        />
      )}

      <UploadProgressList
        uploads={uploads}
        className="mt-4"
        processingLabel={t('upload.processingStatement')}
      />

      {error && (
        <div className="mt-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      {notice && (
        <div className="mt-4 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
          {notice}
        </div>
      )}

      {uploadResult && (
        <UploadSummary
          result={uploadResult}
          companyWarning={multiFirm && uploadResult.companyMatch === 'none'}
          onReview={() =>
            navigate(`/financials/bank-statements/${uploadResult.statementId}/reconcile`)
          }
          onDismiss={() => setUploadResult(null)}
        />
      )}

      {/* Period filter: everything / a month / a year / a free interval */}
      {/* Phones: two equal columns; the reset buttons span the row. */}
      <div className="mt-6 grid grid-cols-2 items-end gap-2 sm:flex sm:flex-wrap">
        <Select
          label={t('bankStatements.filter.mode')}
          value={mode}
          onChange={(v) => patchPeriod({ mode: v as PeriodMode })}
          options={PERIOD_MODES.map((m) => ({
            value: m,
            label: t(`bankStatements.filter.mode.${m}` as 'bankStatements.filter.mode.all'),
          }))}
          className="w-full sm:w-40"
        />
        {(mode === 'month' || mode === 'year') && (
          <Select
            label={t('bankStatements.filter.year')}
            value={String(year)}
            onChange={(v) => patchPeriod({ year: Number(v) })}
            options={years.map((y) => ({ value: String(y), label: String(y) }))}
            className="w-full sm:w-28"
          />
        )}
        {mode === 'month' && (
          <Select
            label={t('bankStatements.filter.month')}
            value={String(month)}
            onChange={(v) => patchPeriod({ month: Number(v) })}
            options={months.map((name, i) => ({ value: String(i + 1), label: name }))}
            className="w-full sm:w-44"
          />
        )}
        {mode === 'custom' && (
          <>
            <div className="w-full sm:w-44">
              <label className="ui-field-label">{t('bankStatements.filter.from')}</label>
              <DateField
                value={from}
                max={to || undefined}
                onChange={(v) => patchRange({ from: v })}
                placeholder={t('bankStatements.filter.anyDate')}
              />
            </div>
            <div className="w-full sm:w-44">
              <label className="ui-field-label">{t('bankStatements.filter.to')}</label>
              <DateField
                value={to}
                min={from || undefined}
                onChange={(v) => patchRange({ to: v })}
                placeholder={t('bankStatements.filter.anyDate')}
              />
            </div>
          </>
        )}
        <Select
          label="Proveniență"
          value={source ?? ''}
          onChange={(v) => patchProvenance({ source: v })}
          clearable
          placeholder="Toate"
          options={[
            { value: 'import', label: 'Din import' },
            { value: 'manual', label: 'Decise de mână' },
            { value: 'auto', label: 'Potrivite automat' },
          ]}
          className="w-full sm:w-44"
        />
        {importBatchId && (
          <button
            type="button"
            onClick={() => patchProvenance({ importBatchId: null })}
            className="col-span-2 rounded border border-brand-300 bg-brand-50 px-3 py-2 text-sm text-brand-700 hover:bg-brand-100 sm:col-span-1"
            title="Arată toate extrasele"
          >
            Doar dintr-un lot de import ✕
          </button>
        )}
        {(filtered || source || importBatchId) && (
          <button
            type="button"
            onClick={() => {
              patchRange({ from: '', to: '' });
              patchPeriod({ mode: 'all' });
              patchProvenance({ source: '', importBatchId: null });
            }}
            className="col-span-2 rounded border border-gray-300 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 sm:col-span-1"
          >
            {t('bankStatements.filter.clear')}
          </button>
        )}
      </div>

      {/* Table */}
      <div className="mt-4">
        {isLoading && <SkeletonTable rows={6} columns={6} />}
        {isError && <p className="text-red-600">Nu s-au putut încărca extrasele.</p>}
        {!isLoading && !isError && rows.length === 0 && (
          <p className="py-12 text-center text-gray-500">
            {filtered || source || importBatchId
              ? t('bankStatements.filter.empty')
              : 'Niciun extras încărcat încă. Încarcă primul tău extras PDF.'}
          </p>
        )}
        {!isLoading && !isError && rows.length > 0 && (
          <FadeIn>
            {/* Below lg: one card per statement; in the eleven-column table the
                IBAN broke one character per line and the period word by word. */}
            <ul className="divide-y divide-gray-100 overflow-hidden rounded-lg border border-gray-200 bg-white lg:hidden">
              {rows.map((s) => {
                const badge = STATUS_BADGE[s.status];
                return (
                  <li key={s.id} className="px-3 py-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <Link
                          to={`/financials/bank-statements/${s.id}/reconcile`}
                          className="font-medium text-brand-700 hover:underline"
                        >
                          {s.bankName}
                        </Link>
                        <div className="truncate text-xs text-gray-500">
                          {s.bankCode === 'cash'
                            ? 'Cash'
                            : [s.accountHolder, s.iban].filter(Boolean).join(' · ') || '—'}
                        </div>
                      </div>
                      <span
                        className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${badge.cls}`}
                      >
                        {badge.label}
                      </span>
                    </div>
                    <dl className="mt-2 grid grid-cols-3 gap-2 text-xs">
                      <div>
                        <dt className="text-gray-500">Credit</dt>
                        <dd className="font-medium tabular-nums text-emerald-600">
                          {s.totalCredits != null ? formatCurrency(s.totalCredits, 'RON') : '—'}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-gray-500">Debit</dt>
                        <dd className="font-medium tabular-nums text-red-600">
                          {s.totalDebits != null ? formatCurrency(s.totalDebits, 'RON') : '—'}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-gray-500">Tranzacții</dt>
                        <dd className="tabular-nums text-gray-900">{s.transactionCount}</dd>
                      </div>
                    </dl>
                    <div className="mt-1.5 flex items-center justify-between gap-2 text-xs text-gray-500">
                      <span className="min-w-0 truncate">
                        {period(s)}
                        {multiFirm && s.company?.name ? ` · ${s.company.name}` : ''}
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          setDeleteError(null);
                          setDeleteTarget(s);
                        }}
                        title={t('bankStatements.delete.button')}
                        aria-label={t('bankStatements.delete.button')}
                        className="shrink-0 rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </li>
                );
              })}
              {data?.totals && (
                <li className="bg-gray-50 px-3 py-2 text-xs text-gray-600">
                  Total creditat:{' '}
                  <span className="font-semibold text-emerald-600">
                    {formatCurrency(data.totals.credits, 'RON')}
                  </span>
                  {' · '}
                  Total debitat:{' '}
                  <span className="font-semibold text-red-600">
                    {formatCurrency(data.totals.debits, 'RON')}
                  </span>
                </li>
              )}
            </ul>
            <div className="hidden overflow-x-auto rounded-lg border border-gray-200 lg:block">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">
                      <SortHeader label="Bancă" {...header('bankName')} />
                    </th>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">
                      <SortHeader label="IBAN" {...header('iban')} />
                    </th>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">Cont</th>
                    {multiFirm && (
                      <th className="px-3 py-2 text-left font-medium text-gray-600">
                        {t('company.switcher.label')}
                      </th>
                    )}
                    <th className="px-3 py-2 text-left font-medium text-gray-600">
                      <SortHeader label="Perioadă" {...header('periodStart')} />
                    </th>
                    <th className="px-3 py-2 text-right font-medium text-gray-600">
                      <SortHeader label="Tranzacții" {...header('transactionCount')} />
                    </th>
                    <th className="px-3 py-2 text-right font-medium text-gray-600">
                      <SortHeader label="Credit" {...header('totalCredits')} />
                    </th>
                    <th className="px-3 py-2 text-right font-medium text-gray-600">
                      <SortHeader label="Debit" {...header('totalDebits')} />
                    </th>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">
                      <SortHeader label="Status" {...header('status')} />
                    </th>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">
                      <SortHeader label="Încărcat" {...header('createdAt')} />
                    </th>
                    <th className="px-3 py-2" aria-label={t('bankStatements.delete.button')} />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {rows.map((s) => {
                    const badge = STATUS_BADGE[s.status];
                    return (
                      <tr key={s.id} className="hover:bg-gray-50">
                        <td className="px-3 py-2">
                          <Link
                            to={`/financials/bank-statements/${s.id}/reconcile`}
                            className="font-medium text-brand-700 hover:underline"
                          >
                            {s.bankName}
                          </Link>
                          {s.accountHolder && (
                            <div className="text-xs text-gray-400">{s.accountHolder}</div>
                          )}
                        </td>
                        <td className="px-3 py-2 font-mono text-xs text-gray-600">
                          {s.bankCode === 'cash' ? (
                            <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/15 px-2 py-0.5 font-sans text-xs font-medium text-amber-700">
                              <Banknote size={12} /> Cash
                            </span>
                          ) : (
                            (s.iban ?? '—')
                          )}
                        </td>
                        <td className="px-3 py-2 text-xs text-gray-600">
                          {s.bankAccount
                            ? (s.bankAccount.label ??
                              s.bankAccount.iban
                                .replace(/(.{4})/g, '$1 ')
                                .trim()
                                .slice(0, 19))
                            : '—'}
                        </td>
                        {multiFirm && (
                          <td className="px-3 py-2 text-gray-600">{s.company?.name ?? '—'}</td>
                        )}
                        <td className="px-3 py-2 text-gray-600">{period(s)}</td>
                        <td className="px-3 py-2 text-right text-gray-700">{s.transactionCount}</td>
                        <td className="px-3 py-2 text-right font-medium text-emerald-600">
                          {s.totalCredits != null ? formatCurrency(s.totalCredits, 'RON') : '—'}
                        </td>
                        <td className="px-3 py-2 text-right font-medium text-red-600">
                          {s.totalDebits != null ? formatCurrency(s.totalDebits, 'RON') : '—'}
                        </td>
                        <td className="px-3 py-2">
                          <span
                            className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${badge.cls}`}
                          >
                            {badge.label}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-gray-500">{formatDate(s.createdAt)}</td>
                        <td className="px-3 py-2 text-right">
                          <button
                            type="button"
                            onClick={() => {
                              setDeleteError(null);
                              setDeleteTarget(s);
                            }}
                            title={t('bankStatements.delete.button')}
                            aria-label={t('bankStatements.delete.button')}
                            className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600"
                          >
                            <Trash2 size={15} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                {data?.totals && (
                  <tfoot className="border-t border-gray-200 bg-gray-50">
                    <tr>
                      <td colSpan={multiFirm ? 11 : 10} className="px-3 py-2 text-right text-sm">
                        <span className="text-gray-600">
                          Total creditat:{' '}
                          <span className="font-semibold text-emerald-600">
                            {formatCurrency(data.totals.credits, 'RON')}
                          </span>
                          {' · '}
                          Total debitat:{' '}
                          <span className="font-semibold text-red-600">
                            {formatCurrency(data.totals.debits, 'RON')}
                          </span>
                        </span>{' '}
                        <span className="text-xs text-gray-400">(toate extrasele filtrate)</span>
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>

            {meta && meta.totalPages > 1 && (
              <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
                <span>
                  Pagina {meta.page} din {meta.totalPages}
                </span>
                <div className="flex gap-2">
                  <button
                    disabled={meta.page <= 1}
                    onClick={() => setPage((p) => p - 1)}
                    className="rounded border border-gray-300 px-3 py-1 disabled:opacity-50"
                  >
                    Anterior
                  </button>
                  <button
                    disabled={meta.page >= meta.totalPages}
                    onClick={() => setPage((p) => p + 1)}
                    className="rounded border border-gray-300 px-3 py-1 disabled:opacity-50"
                  >
                    Următorul
                  </button>
                </div>
              </div>
            )}
          </FadeIn>
        )}
      </div>

      {deleteTarget && (
        <ConfirmDialog
          title={t('bankStatements.delete.title')}
          message={t('bankStatements.delete.message', {
            name: `${deleteTarget.bankName} (${period(deleteTarget)})`,
          })}
          confirmLabel={t('bankStatements.delete.confirm')}
          danger
          busy={deleteMutation.isPending}
          error={deleteError}
          onConfirm={() => void handleDelete()}
          onCancel={() => {
            if (!deleteMutation.isPending) setDeleteTarget(null);
          }}
        />
      )}

      {cashModalOpen && (
        <AddCashPaymentModal
          onClose={() => setCashModalOpen(false)}
          onCreated={() => {
            setCashModalOpen(false);
            setNotice(t('cash.created'));
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Post-upload duplicate summary (CLE-152). Shown when the import skipped or
// flagged duplicates, or the same statement was uploaded before.
// ---------------------------------------------------------------------------

interface UploadSummaryProps {
  result: UploadResult;
  /** Multi-company: the firm couldn't be auto-detected from the PDF's CUI. */
  companyWarning: boolean;
  onReview: () => void;
  onDismiss: () => void;
}

function UploadSummary({ result, companyWarning, onReview, onDismiss }: UploadSummaryProps) {
  const { t } = useI18n();
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-4">
      <div className="flex items-start gap-2">
        <CopyCheck className="mt-0.5 shrink-0 text-amber-600" size={18} />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-amber-900">
            {result.duplicatesSkipped > 0
              ? `${result.duplicatesSkipped} ${
                  result.duplicatesSkipped === 1
                    ? 'tranzacție duplicat detectată'
                    : 'tranzacții duplicat detectate'
                } și omise.`
              : 'Încărcare procesată.'}
          </p>
          <p className="mt-0.5 text-xs text-amber-800">
            {result.importedCount} importate
            {result.possibleDuplicates > 0 &&
              ` · ${result.possibleDuplicates} ${
                result.possibleDuplicates === 1 ? 'posibil duplicat' : 'posibile duplicate'
              } marcate pentru verificare`}
            {result.autoMatched > 0 && ` · ${result.autoMatched} potrivite automat`}
          </p>
          {result.previousUpload && (
            <p className="mt-1 text-xs text-amber-700">
              Acest extras pare să fi fost încărcat anterior
              {result.previousUpload.fileName
                ? ` („${result.previousUpload.fileName}”)`
                : ''} pe {formatDate(result.previousUpload.uploadedAt)}. Tranzacțiile existente au
              fost omise.
            </p>
          )}
          {companyWarning && (
            <p className="mt-1 text-xs font-medium text-amber-800">
              {t('bank.company.noneWarning')}
            </p>
          )}
          {result.bankAccountMatch === 'new_suggested' && (
            <p className="mt-1 text-xs text-blue-700">
              IBAN-ul din extras nu este asociat niciunui cont bancar.{' '}
              <Link to="/settings" className="font-medium underline hover:text-blue-900">
                Adaugă contul bancar în Setări → Conturi bancare
              </Link>{' '}
              pentru a activa detectia transferurilor interne.
            </p>
          )}
          {result.warnings.length > 0 && (
            <ul className="mt-1 list-inside list-disc text-xs text-amber-700">
              {result.warnings.map((w, i) => (
                <li key={i}>{w}</li>
              ))}
            </ul>
          )}

          {result.duplicates.length > 0 && (
            <div className="mt-2">
              <button
                type="button"
                onClick={() => setExpanded((e) => !e)}
                className="text-xs font-medium text-amber-800 underline hover:text-amber-900"
              >
                {expanded ? 'Ascunde' : 'Arată'} tranzacțiile omise ({result.duplicates.length})
              </button>
              {expanded && (
                <div className="mt-2 overflow-x-auto rounded border border-amber-200 bg-white">
                  <table className="min-w-full divide-y divide-amber-100 text-xs">
                    <thead className="bg-amber-100/50 text-amber-800">
                      <tr>
                        <th className="px-2 py-1.5 text-left font-medium">Dată</th>
                        <th className="px-2 py-1.5 text-left font-medium">Contraparte</th>
                        <th className="px-2 py-1.5 text-right font-medium">Sumă</th>
                        <th className="px-2 py-1.5 text-left font-medium">Motiv</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-amber-50 text-gray-700">
                      {result.duplicates.map((d, i) => (
                        <tr key={i}>
                          <td className="whitespace-nowrap px-2 py-1.5">{formatDate(d.date)}</td>
                          <td className="px-2 py-1.5">{d.counterparty ?? d.description ?? '—'}</td>
                          <td
                            className={`whitespace-nowrap px-2 py-1.5 text-right font-medium ${
                              d.type === 'credit' ? 'text-emerald-600' : 'text-red-600'
                            }`}
                          >
                            {d.type === 'credit' ? '+' : '−'}
                            {formatCurrency(d.amount, 'RON')}
                          </td>
                          <td className="px-2 py-1.5 text-gray-500">
                            {d.verdict === 'possible'
                              ? 'Posibil duplicat'
                              : d.originalImportedAt
                                ? `Deja importat pe ${formatDate(d.originalImportedAt)}`
                                : 'Deja importat'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={onReview}
              className="rounded-md bg-brand-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-700"
            >
              Verifică extrasul
            </button>
            <button
              type="button"
              onClick={onDismiss}
              className="rounded-md border border-amber-300 px-3 py-1.5 text-xs font-medium text-amber-800 hover:bg-amber-100"
            >
              Închide
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
